import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../../../../components/Button';
import { TextInputFormField } from '../../../../../../components/TextInputFormField';
import { XIcon } from '../../../../../../components/svgs/icons/XIcon';
import { charmGray } from '../../../../../../styles/colors';
import { bs } from '../../../../../../styles/styles';
import { styleSheet } from '../styles';

interface FieldOptionsProps {
	field: Api.IFormField<string>;
	questionIndex: number;
	onOptionChange: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;
	onRemoveOption: (index: number) => void;
	onAddOption: () => void;
	onAfterItem?: (optionLabel: string) => React.ReactNode;
}

const FieldOptionsInner = React.memo<FieldOptionsProps>(
	({ field, questionIndex, onOptionChange, onRemoveOption, onAddOption, onAfterItem }) => {
		// Memoize expensive calculations
		const fieldOptions = React.useMemo(() => field?.options || [], [field?.options]);
		const fieldType = field.fieldType;
		const isMultipleOptions = fieldType === Api.FormFieldType.MultipleOptions;
		const isOption = fieldType === Api.FormFieldType.Option;
		const showRemoveButtons = fieldOptions.length > 2;

		// Memoize wrapper className
		const wrapperClassName = React.useMemo(
			() => css(styleSheet.optionsWrapper, isOption ? styleSheet.optionsWrapperDropdown : undefined),
			[isOption]
		);

		// Memoize add button className
		const addButtonClassName = React.useMemo(
			() => css(styleSheet.addOptionButton, isMultipleOptions ? styleSheet.addOptionButtonLeftPadding : null),
			[isMultipleOptions]
		);

		if (!isOption && !isMultipleOptions) {
			return null;
		}

		return (
			<div className={wrapperClassName}>
				{fieldOptions.map((fieldOption, optionIndex) => {
					const fieldOptionValue = fieldOption.label;
					const hasValueAndAfterInputItem = fieldOptionValue && onAfterItem;

					// Memoize option-specific values
					const optionKey = `edit-custom-${fieldOption.fieldId}-question-${questionIndex}-input-${optionIndex}`;
					const placeholder = isOption ? `Option ${optionIndex + 1}` : `Response ${optionIndex + 1}`;

					return (
						<div className={css(bs.flex, bs.itemsCenter, bs.gap2)} key={optionKey}>
							{isMultipleOptions ? <div className={css(styleSheet.fakeCheckbox)} /> : null}
							<div className={css(bs.fullWidth)}>
								<div className={css(bs.fullWidth, bs.relative)}>
									<TextInputFormField
										key={optionKey}
										inputId={optionKey}
										labelStyles={[styleSheet.label]}
										placeholder={placeholder}
										onChange={e => onOptionChange(e, optionIndex)}
										onKeyDown={e => {
											if (e.key === ',') {
												e.preventDefault();
												toast.error('Commas are not allowed in option labels');
											}
										}}
										type='text'
										styles={[
											styleSheet.questionInput,
											hasValueAndAfterInputItem ? styleSheet.afterInputItem : undefined,
										]}
										id={optionKey}
										value={fieldOptionValue}
									/>
									{hasValueAndAfterInputItem ? onAfterItem(fieldOptionValue) : null}
								</div>
							</div>
							{showRemoveButtons ? (
								<div>
									<Button
										kind='custom'
										type='button'
										className={css(styleSheet.removeOptionButton)}
										label={
											<>
												<span className={css(styleSheet.removeOptionButtonText)}>Remove Option</span>
												<XIcon color={charmGray} width={16} height={16} />
											</>
										}
										onClick={() => onRemoveOption(optionIndex)}
										data-testid='remove-option'
									/>
								</div>
							) : null}
						</div>
					);
				})}
				{fieldOptions.length ? (
					<Button
						kind='custom'
						type='button'
						label='+ Add Option'
						className={addButtonClassName}
						onClick={() => onAddOption()}
					/>
				) : null}
			</div>
		);
	}
);

FieldOptionsInner.displayName = 'FieldOptionsInner';

export const FieldOptions = FieldOptionsInner;
